import { toE164, formatPhone } from './phone';
import { entriesOf } from './contactMethods';

// BL-177 PR C: CSV in and out for company contacts. CSV upload only: the imported provider has no contacts API endpoint (and its key never touches
// the browser). Reads the imported provider's contact export by header names, or any generic CSV through a column mapping the admin can change.

// ---------------------------------------------------------------- parsing (RFC 4180: quotes, doubled quotes, CRLF, BOM)
export function parseCsv(text) {
    const source = String(text || '').replace(/^﻿/, '');
    const rows = [];
    let row = []; let field = ''; let inQuotes = false;
    for (let i = 0; i < source.length; i += 1) {
        const ch = source[i];
        if (inQuotes) {
            if (ch === '"') {
                if (source[i + 1] === '"') { field += '"'; i += 1; } else inQuotes = false;
            } else field += ch;
        } else if (ch === '"') inQuotes = true;
        else if (ch === ',') { row.push(field); field = ''; }
        else if (ch === '\n' || ch === '\r') {
            if (ch === '\r' && source[i + 1] === '\n') i += 1;
            row.push(field); field = '';
            if (row.some(cell => cell.trim() !== '')) rows.push(row);
            row = [];
        } else field += ch;
    }
    row.push(field);
    if (row.some(cell => cell.trim() !== '')) rows.push(row);
    if (!rows.length) return { headers: [], rows: [] };
    const headers = rows[0].map((h, index) => (h.trim() || `Column ${index + 1}`));
    return {
        headers,
        rows: rows.slice(1).map((cells, rowIndex) => ({
            __row: rowIndex + 2, // the line in the file (header is line 1)
            ...Object.fromEntries(headers.map((h, i) => [h, (cells[i] ?? '').trim()])),
        })),
    };
}

// ---------------------------------------------------------------- column mapping
const norm = (header) => header.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const has = (header, re) => re.test(norm(header));

const labelFromHeader = (header) => {
    const h = norm(header);
    if (/\b(mobile|cell)\b/.test(h)) return 'mobile';
    if (/\b(work|office|business)\b/.test(h)) return 'work';
    if (/\bhome\b/.test(h)) return 'home';
    return 'other';
};

/** Best guess at the mapping from the file's headers. the imported provider's export (first/last name, phone, email, company, tags, opt-out) is covered by the same aliases. */
export function guessMapping(headers) {
    const mapping = {
        name: '', first_name: '', last_name: '', account_name: '', account_kind: '', title: '', tags: '', notes: '', opted_out: '',
        phones: [], emails: [], addresses: [],
    };
    for (const header of headers) {
        const h = norm(header);
        if (!mapping.first_name && /^(first name|firstname|given name|first)$/.test(h)) mapping.first_name = header;
        else if (!mapping.last_name && /^(last name|lastname|surname|family name|last)$/.test(h)) mapping.last_name = header;
        else if (!mapping.name && /^(name|full name|contact name|contact|display name)$/.test(h)) mapping.name = header;
        else if (!mapping.account_name && /^(company|company name|account|account name|organization|organisation|business|business name)$/.test(h)) mapping.account_name = header;
        else if (!mapping.account_kind && /^(account type|type of account|client type)$/.test(h)) mapping.account_kind = header;
        else if (!mapping.title && /^(title|job title|position|role)$/.test(h)) mapping.title = header;
        else if (!mapping.tags && /^(tags|labels|groups|segments)$/.test(h)) mapping.tags = header;
        else if (!mapping.notes && /^(notes?|comments?|memo)$/.test(h)) mapping.notes = header;
        else if (!mapping.opted_out && /(unsubscribed|opted out|opt out|do not (text|contact|call)|dnd|stop|blocked)/.test(h)) mapping.opted_out = header;
        else if (has(header, /(e mail|email)/)) mapping.emails.push({ column: header, label: labelFromHeader(header) === 'other' ? 'work' : labelFromHeader(header) });
        else if (has(header, /(phone|mobile|cell|tel\b|telephone|whatsapp)/)) mapping.phones.push({ column: header, label: labelFromHeader(header) === 'other' && /phone|tel/.test(h) ? 'mobile' : labelFromHeader(header) });
        else if (has(header, /(address|street|city|zip|postal)/) && !/e mail|email/.test(h)) mapping.addresses.push({ column: header, label: labelFromHeader(header) === 'other' ? 'home' : labelFromHeader(header) });
    }
    return mapping;
}

const TRUTHY = /^(1|true|yes|y|opted out|unsubscribed|stop|stopped|blocked|dnd|do not contact|do not text)$/i;

/** One import row per CSV line, in the shape crm_import_contacts_run expects. Phones go in E.164 when libphonenumber can read them. */
export function buildImportRows(parsed, mapping, defaultCountry = 'US') {
    const pick = (row, column) => (column ? (row[column] || '').trim() : '');
    return parsed.rows.map((row) => {
        const first = pick(row, mapping.first_name);
        const last = pick(row, mapping.last_name);
        const name = pick(row, mapping.name) || [first, last].filter(Boolean).join(' ');
        const kindText = pick(row, mapping.account_kind).toLowerCase();
        const accountKind = /personal|individual|person/.test(kindText) ? 'personal' : /business|company|org/.test(kindText) ? 'business' : null;
        const entries = (spec, transform = v => v) => (spec || [])
            .map(({ column, label }) => ({ label, value: transform(pick(row, column)) }))
            .filter(e => e.value);
        const tagsText = pick(row, mapping.tags);
        return {
            row: row.__row,
            name,
            account_name: pick(row, mapping.account_name),
            account_kind: accountKind,
            title: pick(row, mapping.title),
            phones: entries(mapping.phones, v => toE164(v, defaultCountry) || v),
            emails: entries(mapping.emails),
            addresses: (mapping.addresses || []).length
                ? [{ label: mapping.addresses[0].label, text: mapping.addresses.map(a => pick(row, a.column)).filter(Boolean).join(', ') }].filter(a => a.text)
                : [],
            tags: tagsText ? tagsText.split(/[;,|]/).map(t => t.trim()).filter(Boolean) : [],
            notes: pick(row, mapping.notes),
            opted_out: mapping.opted_out ? TRUTHY.test(pick(row, mapping.opted_out)) : false,
        };
    });
}

// ---------------------------------------------------------------- writing CSV
export const csvCell = (value) => {
    const text = value === null || value === undefined ? '' : String(value);
    const safe = /^[=+\-@\t\r]/.test(text) && !/^\+?[0-9 ()-]+$/.test(text) ? `'${text}` : text; // keep spreadsheets from running a cell as a formula
    return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};
export const toCsv = (headers, rows) => [headers, ...rows].map(r => r.map(csvCell).join(',')).join('\r\n');

/** Export: one row per account (with its primary contact) from whatever the screen currently shows. */
export function buildExportCsv(accounts) {
    const people = accounts.map(account => ({ account, contact: account.primary_contact }));
    const most = (kind) => Math.max(1, ...people.map(({ contact }) => entriesOf(contact, kind).filter(e => (e.value || '').trim()).length));
    const phoneCount = most('phone'); const emailCount = most('email'); const addressCount = most('address');
    const headers = ['Name', 'Account name', 'Account type', 'Title', 'Account status', 'Tags', 'SMS opted out', 'Source', 'Created', 'Updated'];
    for (let i = 1; i <= phoneCount; i += 1) headers.push(`Phone ${i} label`, `Phone ${i} E.164`, `Phone ${i} formatted`, `Phone ${i} primary`);
    for (let i = 1; i <= emailCount; i += 1) headers.push(`Email ${i} label`, `Email ${i}`, `Email ${i} primary`);
    for (let i = 1; i <= addressCount; i += 1) headers.push(`Address ${i} label`, `Address ${i}`, `Address ${i} primary`);
    const rows = people.map(({ account, contact }) => {
        const row = [
            contact?.name || '', account.account_name || '',
            (account.account_kind || (account.is_business ? 'business' : 'personal')) === 'personal' ? 'Personal' : 'Business',
            contact?.position || '', account.status || '', (contact?.tags || []).join('; '),
            contact?.sms_opted_out_at ? 'Yes' : 'No', contact?.source || account.source || '',
            contact?.created_at || account.created_at || '', contact?.updated_at || '',
        ];
        const phones = entriesOf(contact, 'phone').filter(e => (e.value || '').trim());
        const emails = entriesOf(contact, 'email').filter(e => (e.value || '').trim());
        const addresses = entriesOf(contact, 'address').filter(e => (e.value || '').trim());
        for (let i = 0; i < phoneCount; i += 1) {
            const e = phones[i];
            row.push(e?.label || '', e ? (toE164(e.value, 'US') || e.value) : '', e ? formatPhone(e.value) : '', e ? (e.is_primary ? 'Yes' : 'No') : '');
        }
        for (let i = 0; i < emailCount; i += 1) { const e = emails[i]; row.push(e?.label || '', e?.value || '', e ? (e.is_primary ? 'Yes' : 'No') : ''); }
        for (let i = 0; i < addressCount; i += 1) { const e = addresses[i]; row.push(e?.label || '', e?.value || '', e ? (e.is_primary ? 'Yes' : 'No') : ''); }
        return row;
    });
    return toCsv(headers, rows);
}

/** The downloadable report after an import (or its preview): one line per file row, then the invalid entries and conflicts. */
export function buildImportReportCsv(report) {
    const rows = (report.results || []).map(r => [r.row, r.action, r.name || '', r.account || '', r.note || '', '']);
    (report.invalid || []).forEach(i => rows.push([i.row, 'invalid', '', '', `${i.kind}: ${i.value}`, '']));
    (report.conflicts || []).forEach(c => rows.push([c.row, 'conflict', c.existing, '', `${c.field}: file says "${c.file}", kept "${c.existing}"`, '']));
    return toCsv(['Row', 'Result', 'Person', 'Account', 'Note', ''], rows.map(r => r.slice(0, 5)));
}

export function downloadCsv(filename, text) {
    const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
